import { cn } from "@d12labs/utils";

/**
 * A tiny inline-SVG trend line (no axes/labels) with an optional gradient fill
 * and an end-point dot. Stretches to its container width via preserveAspectRatio.
 */
export function Sparkline({
	values,
	height = 36,
	width = 120,
	color = "var(--color-ink-muted)",
	fill = true,
	className,
}: {
	values: number[];
	height?: number;
	width?: number;
	color?: string;
	fill?: boolean;
	/** Extra classes applied to the SVG element. Overrides the default `block h-9 w-full`. */
	className?: string;
}) {
	if (!values || values.length < 2) return null;

	const min = Math.min(...values, 0);
	const max = Math.max(...values, 100);
	const padX = 2;
	const padY = 4;
	const dx = (width - padX * 2) / (values.length - 1);
	const yOf = (v: number) =>
		height - padY - ((v - min) / Math.max(1, max - min)) * (height - padY * 2);

	const path = values.map((v, i) => `${i === 0 ? "M" : "L"} ${padX + i * dx} ${yOf(v)}`).join(" ");
	const lastX = padX + (values.length - 1) * dx;
	const lastY = yOf(values[values.length - 1] ?? 0);
	const area = `${path} L ${lastX} ${height - padY} L ${padX} ${height - padY} Z`;
	const gradientId = `spark-${color.replace(/[^a-z0-9]/gi, "")}`;

	return (
		<svg
			className={cn("block w-full", className)}
			style={{ height }}
			viewBox={`0 0 ${width} ${height}`}
			preserveAspectRatio="none"
			aria-hidden="true"
		>
			{fill ? (
				<defs>
					<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor={color} stopOpacity="0.18" />
						<stop offset="100%" stopColor={color} stopOpacity="0" />
					</linearGradient>
				</defs>
			) : null}
			{fill ? <path d={area} fill={`url(#${gradientId})`} /> : null}
			<path
				d={path}
				fill="none"
				stroke={color}
				strokeWidth="1.4"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<circle cx={lastX} cy={lastY} r="2.2" fill={color} />
		</svg>
	);
}
